import { List, type ListItemProps, setRef, Skeleton, Stack, styled } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { equals, times } from "ramda";
import { isArray, isNilOrEmpty, isPromise, isNotNilOrEmpty } from "ramda-adjunct";
import React, { forwardRef, type Ref, useEffect, useRef } from "react";
import { ListItemWrapper } from "@sinch/components/form/MultilevelSelect/ListItemWrapper";
import { ListColumnProps } from "@sinch/components/form/MultilevelSelect/types";

/**
 * Props interface for the ListColumnBase component.
 */
interface ListColumnBaseProps<TValue = any> {
  /** The list of items to display in the column */
  list: ListColumnProps<TValue>["list"];
  /** Callback fired when mouse pointer hovers over a list item, param is index of item in list */
  onMouseOver?: (index: number) => void;
  /** Callback fired when a list item is clicked, param is index of item in list */
  onClick?: (index: number) => void;
  /** Callback fired when the selected value changes */
  onChange?: (e: React.MouseEvent<HTMLLIElement>, value: TValue) => void;
  /** CSS class name to apply to the component */
  className?: string;
  /** Index of the currently selected list item */
  selectedIndex?: number | null;
  /** Currently selected value */
  selectedValue: TValue | null;
  /** Function to get additional ListItem props for a value */
  getListItemProps?: (value: TValue) => ListItemProps;
}

/**
 * Base component for rendering a column of selectable list items.
 * Used as part of a multi-level select component.
 */
const ListColumnBase = <TValue,>(
  {
    list,
    onMouseOver,
    className,
    selectedIndex,
    getListItemProps,
    onChange,
    onClick,
    selectedValue,
  }: ListColumnBaseProps<TValue>,
  listRef: Ref<HTMLUListElement>
) => {
  const selectedRef = useRef<HTMLLIElement>(null);

  /**
   * Handles click events on list items
   */
  const handleClick = (canChange: boolean) => (e: React.MouseEvent<HTMLLIElement>, val: TValue, index: number) => {
    onClick?.(index);
    if (canChange) {
      onChange?.(e, val);
    }
  };

  // Scroll to the selected item when component mounts
  useEffect(() => {
    if (selectedRef) {
      selectedRef.current?.scrollIntoView({ block: "center" });
    }
  }, []);

  return (
    <Grid className={className} size="grow" sx={{ height: "inherit", overflowY: "auto" }}>
      {isPromise(list) && (
        <Stack spacing={1} sx={{ p: 1 }}>
          {times(
            (ix) => (
              <Skeleton key={ix} height={45 - 8} variant="rounded" width="100%" />
            ),
            5
          )}
        </Stack>
      )}
      {isArray(list) && (
        <List ref={listRef} role="listbox">
          {list.map(({ value, label, children }, ix) => (
            <ListItemWrapper<TValue>
              key={ix}
              ref={(el) => (selectedIndex === ix ? setRef(selectedRef, el) : null)}
              hasChildren={isNotNilOrEmpty(children)}
              hoverState={ix === selectedIndex}
              label={label}
              listItemProps={{ ...getListItemProps?.(value), tabIndex: ix, role: "option" }}
              onClick={(e, val) => handleClick(isNilOrEmpty(children))(e, val, ix)}
              onMouseOver={() => onMouseOver?.(ix)}
              selected={equals(selectedValue, value)}
              value={value}
            />
          ))}
        </List>
      )}
    </Grid>
  );
};

/**
 * A styled list column component for use in multi-level selection interfaces.
 * Enhances the base component with additional styling.
 */
export const ListColumn = styled(forwardRef(ListColumnBase))(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderRight: "none",
  },
  "& li.Mui-focused": {
    backgroundColor: "rgba(18, 122, 170, 0.08)",
  },
})) as typeof ListColumnBase;
